import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import LanguageContext from "../helper/LanguageContext";
import MaterialIcon from "./MaterialIcon";
import CompanyTrialEndedNote from "./CompanyTrialEndedNote";
import TrialCountdownText from "./TrialCountdownText";
import "./TrialWarningBanner.css";
import {
  isCompanyManagedKjuupIo,
  isCompanyTrialEnded,
  navigateToKjuupIoBilling,
  shouldShowCompanyTrialUpgradeNotes,
} from "../utils/kjuupIoBillingNav";
import { resolveStripeFlags } from "../utils/stripeFeature";

// Fallback only — the real length comes from the status payload (Admin Center).
const DEFAULT_TRIAL_TOTAL_DAYS = 30;

// v3: rich card layout for personal trials (7 / 3 / 1).
const dismissKey = (n) => `kjuup_io_trial_banner_v3_dismissed_${n}`;

/**
 * Map remaining days onto the 7 / 3 / 1 milestones.
 * Fully ended company trials use CompanyTrialEndedNote instead.
 */
function resolveThreshold(days) {
  const n = Math.ceil(Number(days));
  if (!Number.isFinite(n) || n < 0) return null;
  if (n <= 1) return 1;
  if (n <= 3) return 3;
  if (n <= 7) return 7;
  return null;
}

function readDaysRemaining(subscription) {
  if (!subscription) return null;
  const raw =
    subscription.daysRemaining != null
      ? subscription.daysRemaining
      : subscription.trialDaysRemaining != null
        ? subscription.trialDaysRemaining
        : null;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Trial warning card (7 / 3 / 1 day milestones).
 * - Personal Stripe trials
 * - Company trials: company admins with a seat only (CTA → Admin Panel)
 * Regular Kjuup+ employees never see this during a company trial.
 */
export default function TrialWarningBanner({ style }) {
  const history = useHistory();
  const { strings } = useContext(LanguageContext);
  const profile = useSelector((s) => s.profile);
  const subscription = useSelector((s) => s.subscription) || {};
  const { stripeUiEnabled } = resolveStripeFlags(profile, subscription);
  const days = readDaysRemaining(subscription);
  const companyManaged = isCompanyManagedKjuupIo(subscription);
  const companyEnded = isCompanyTrialEnded(subscription);
  const companyAdminTrialNotes =
    shouldShowCompanyTrialUpgradeNotes(subscription);
  const personalTrialing =
    !companyEnded &&
    !companyManaged &&
    (!!subscription.trialing ||
      !!subscription.isTrialing ||
      subscription.subscriptionStatus === "trialing" ||
      (days != null && !!subscription.active));
  const trialing = personalTrialing || companyAdminTrialNotes;
  const lastFetchedAt = subscription.lastFetchedAt;
  const [visibleThreshold, setVisibleThreshold] = useState(null);

  useEffect(() => {
    if (!stripeUiEnabled || companyEnded) {
      setVisibleThreshold(null);
      return;
    }
    // Non-admin company seats: no upgrade card during trial.
    if (companyManaged && !companyAdminTrialNotes) {
      setVisibleThreshold(null);
      return;
    }
    if (!trialing || days == null) {
      setVisibleThreshold(null);
      return;
    }
    const threshold = resolveThreshold(days);
    if (threshold == null) {
      setVisibleThreshold(null);
      return;
    }
    try {
      localStorage.removeItem(`kjuup_io_trial_banner_dismissed_${threshold}`);
      localStorage.removeItem(`kjuup_io_trial_banner_v2_dismissed_${threshold}`);
      const dismissed = localStorage.getItem(dismissKey(threshold));
      setVisibleThreshold(dismissed ? null : threshold);
    } catch (e) {
      setVisibleThreshold(threshold);
    }
  }, [
    stripeUiEnabled,
    trialing,
    days,
    lastFetchedAt,
    companyEnded,
    companyManaged,
    companyAdminTrialNotes,
  ]);

  if (!stripeUiEnabled) return null;

  // Company trial fully ended — admin-names note (not the personal upgrade card).
  if (companyEnded) {
    return <CompanyTrialEndedNote compact style={style} />;
  }

  if (visibleThreshold == null) return null;

  const dayNum = Math.max(0, Math.ceil(Number(days)));
  const totalDays = subscription.trialPeriodDays || DEFAULT_TRIAL_TOTAL_DAYS;
  const progressPct = Math.min(
    100,
    Math.max(0, ((totalDays - dayNum) / totalDays) * 100)
  );

  const title =
    dayNum <= 1
      ? strings.kjuup_io_trial_card_title_today ||
        "Your Kjuup.io trial ends today"
      : (strings.kjuup_io_trial_card_title ||
          "Your Kjuup.io trial ends in {days} days").replace(
          "{days}",
          String(dayNum)
        );

  const subtitle =
    strings.kjuup_io_trial_card_subtitle ||
    "Upgrade now to keep using all features.";

  const progressLabel = (
    strings.kjuup_io_trial_card_progress ||
    "{days} of {total} trial days remaining"
  )
    .replace("{days}", String(dayNum))
    .replace("{total}", String(totalDays));

  const ctaLabel =
    companyAdminTrialNotes
      ? strings.kjuup_io_subscribe_now ||
        strings.kjuup_io_trial_card_cta ||
        "Subscribe now"
      : strings.kjuup_io_trial_card_cta ||
        strings.kjuup_io_subscribe_now ||
        "Upgrade now";

  const daysUnit =
    dayNum === 1
      ? strings.kjuup_io_trial_card_day_unit || "DAY"
      : strings.kjuup_io_trial_card_days_unit || "DAYS";

  const onDismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(dismissKey(visibleThreshold), "1");
    } catch (err) {
      /* ignore */
    }
    setVisibleThreshold(null);
  };

  return (
    <div
      className="kjuup-trial-card"
      data-testid="trialWarningBanner"
      data-days={dayNum}
      data-threshold={visibleThreshold}
      data-company-admin={companyAdminTrialNotes ? "1" : "0"}
      style={style}
    >
      <div className="kjuup-trial-card__accent" aria-hidden />
      <div className="kjuup-trial-card__badge" aria-hidden>
        <span className="kjuup-trial-card__badge-num">{dayNum}</span>
        <span className="kjuup-trial-card__badge-unit">{daysUnit}</span>
      </div>
      <div className="kjuup-trial-card__body">
        <div className="kjuup-trial-card__title">{title}</div>
        <div className="kjuup-trial-card__subtitle">{subtitle}</div>
        <div className="kjuup-trial-card__progress-track" aria-hidden>
          <div
            className="kjuup-trial-card__progress-fill trial-warning-banner__progress"
            style={{ "--trial-warning-progress": `${progressPct}%` }}
          />
        </div>
        {dayNum <= 1 ? (
          <TrialCountdownText className="kjuup-trial-card__progress-label" />
        ) : (
          <div className="kjuup-trial-card__progress-label">{progressLabel}</div>
        )}
      </div>
      <button
        type="button"
        className="kjuup-trial-card__cta"
        onClick={() => navigateToKjuupIoBilling(history, subscription)}
      >
        {ctaLabel}
      </button>
      <button
        type="button"
        className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kjuup-trial-card__dismiss"
        onClick={onDismiss}
        aria-label={strings.kjuup_io_banner_dismiss || "Dismiss"}
      >
        <MaterialIcon icon="close" size={18} color="#9a9a9a" />
      </button>
    </div>
  );
}
